import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { capitalize } from 'lodash';

import {
  ConnectionNode,
  DatabaseNode,
  DataPlatform,
  SchemaNode,
  SearchFieldType,
  SearchPlatform,
  TableNode,
  ViewNode,
  WarehouseEntityType,
} from '@/__generated__/graphql';
import { Page } from '@/components/Page';
import { isWarehouseAssetId } from '@/context/AccountNavigation/utils';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useTrackRecentlyViewed } from '@/hooks/admin/recentlyViewed';
import { useAccountSearchResultsWrapper } from '@/hooks/discovery/accountSearchWrapper';
import { useWarehouseEntity } from '@/hooks/discovery/catalog/entities';
import { useValidatedParams } from '@/hooks/params';
import { AssetDoesNotExist } from '../../../../components/ErrorScreens/AssetDoesNotExist';
import { MergedAssetDetailPage } from './components/DetailPages/MergedAssetDetailPage';
import { TableDetailPage } from './components/DetailPages/TableDetailPage';
import { ViewDetailPage } from './components/DetailPages/ViewDetailPage';
import { AssetFilterPage } from './components/FilterPages/AssetFilterPage';
import { SchemaFilterPage } from './components/FilterPages/SchemaFilterPage';

// This is just a placeholder for the warehouse entity since we don't have an entity node for it
const getDummyWarehouseEntity = (
  platform: DataPlatform,
  accountId: number,
): ConnectionNode => ({
  type: WarehouseEntityType.Connection,
  urn: '',
  name: capitalize(platform),
  accountId,
  created: new Date().toISOString(),
  lastSyncedAt: new Date().toISOString(),
  catalogPath: [],
  platform: null,
});

export const AssetDetailsPage = () => {
  const { accountId, assetId } = useValidatedParams();
  const isDataPlatform = isWarehouseAssetId(assetId);

  const entityQuery = useWarehouseEntity({
    accountId,
    filter: {
      parent_urn: null,
      type: null,
      urn: assetId || null,
    },
    pause: isWarehouseAssetId(assetId),
  });

  const assetDoesNotExist = useMemo(
    () =>
      !isDataPlatform &&
      !entityQuery.fetching &&
      !Object.keys(entityQuery.data ?? {}).length,
    [isDataPlatform, entityQuery.fetching, entityQuery.data],
  );

  const assetPage = useMemo(() => {
    const entity = isWarehouseAssetId(assetId)
      ? {
          ...getDummyWarehouseEntity(assetId as DataPlatform, accountId ?? 0),
        }
      : entityQuery.data;
    const entityType = entity?.type;
    if (!accountId) return null;
    switch (entityType) {
      case WarehouseEntityType.Schema:
        return <SchemaFilterPage asset={entity as SchemaNode} />;
      case WarehouseEntityType.Table:
      case WarehouseEntityType.View:
        return <DatasetDetailPage asset={entity as TableNode | ViewNode} />;
      // defaulting to asset filter page so we can have a consistent loading state
      case WarehouseEntityType.Connection:
      case WarehouseEntityType.Database:
      default:
        return (
          <AssetFilterPage
            asset={entity as DatabaseNode | SchemaNode | ConnectionNode}
          />
        );
    }
  }, [entityQuery.data, accountId, assetId]);

  if (entityQuery.fetching) return null;
  return (
    <Page title="Asset Details Page" pageType="assetDetails">
      {assetDoesNotExist ? <AssetDoesNotExist /> : assetPage}
    </Page>
  );
};

const DatasetDetailPage = ({ asset }: { asset: TableNode | ViewNode | undefined }) => {
  const { accountId } = useDbtCloudAccount();
  const url = useLocation().pathname;

  useTrackRecentlyViewed(
    {
      accountId,
      url,
      name: asset?.name ?? '',
      description: '',
      resourceType: asset?.platform?.toLowerCase() ?? 'unknown',
    },
    asset === undefined,
  );

  const searchQuery = useAccountSearchResultsWrapper({
    accountId,
    filter: {
      assetTypes: [SearchPlatform.Dbt, SearchPlatform.Snowflake],
      urns: asset?.urn ? [asset.urn] : [],
      fields: [SearchFieldType.Name],
      query: '',
      resourceTypes: [],
      tags: [],
      materializationTypes: null,
      modelingLayers: null,
      accessLevels: null,
      dbtEnvs: null,
      projectIds: null,
      warehouseEntityTypes: null,
      environmentIds: null,
      uniqueIds: null,
    },
    pageSize: 1,
    pause: !asset?.urn,
  });

  const isMerged = useMemo(
    () =>
      searchQuery.data?.some(
        (result) => result.hit.dbtAsset && result.hit.warehouseAsset,
      ),
    [searchQuery.data],
  );

  if (!asset || searchQuery.fetching) return null;
  if (isMerged) {
    const dbtAsset = searchQuery.data?.[0]?.hit.dbtAsset;
    const warehouseAsset = searchQuery.data?.[0]?.hit.warehouseAsset;
    return (
      <MergedAssetDetailPage dbtAsset={dbtAsset} warehouseAsset={warehouseAsset} />
    );
  }

  switch (asset?.type) {
    case WarehouseEntityType.Table:
      return <TableDetailPage asset={asset as TableNode} />;
    case WarehouseEntityType.View:
      return <ViewDetailPage asset={asset as ViewNode} />;
    default:
      return null;
  }
};
